import { redirect } from '@sleepstation/utils'
import { flip, gt } from 'ramda'
import React, { useContext, useLayoutEffect } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { useMutation } from 'urql'
import UserContext from '../../../context/UserContext'
import { CreateInsomniaTherapyFeedbackMutationForEndOfTherapyMutation } from '../../../graphql/documents'
import useDisplayError from '../../../hooks/useDisplayError'
import PageNavContainer from '../../../state/PageNavContainer'
import FeedbackEndOfTherapyPage from './FeedbackEndOfTherapyPage'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const PAGE_ID = `feedback-end-of-therapy`

const ERROR_MESSAGE = `There was a problem submitting your feedback. Please try again later`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const isGoodRating = flip(gt)(4)

const handleCreateFeedbackSuccess =
  ({ urlPositive, urlDefault }, navigate) =>
  (score) => {
    if (isGoodRating(score)) {
      navigate(urlPositive)
    } else {
      redirect(urlDefault)
    }
  }

const getComments = ({
  'feedback-good': feedbackGood = {},
  'feedback-poor': feedbackPoor = {},
  'feedback-moderate': feedbackModerate = {},
}) => feedbackModerate.answer || feedbackGood.answer || feedbackPoor.answer

const getVariablesFromUserData = ({ rating, ...rest }) => {
  const comments = getComments(rest)

  return {
    comments,
    score: rating.answer,
  }
}

const handleFeedbackComplete =
  (
    enrolmentId,
    createFeedback,
    displayError,
    handleCreateFeedbackSuccessWithUrls
  ) =>
  ({ userData }) => {
    const variables = getVariablesFromUserData(userData)
    createFeedback({ enrolmentId, ...variables })
      .then((result) => {
        const { data, error } = result

        if (error) {
          // This is a GraphQL error
          displayError(ERROR_MESSAGE, error, { isFatal: true })
        } else {
          const {
            createInsomniaTherapyFeedback: { score },
          } = data
          handleCreateFeedbackSuccessWithUrls(score)
        }
      })
      .catch((error) => {
        // This is a network error
        displayError(ERROR_MESSAGE, error, { isFatal: true })
      })
  }

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const FeedbackEndOfTherapyModule = () => {
  const loaderData = useLoaderData()
  const { displayError } = useDisplayError()
  const navigate = useNavigate()
  const { currentEnrolment } = useContext(UserContext)
  const [, setNav] = PageNavContainer.useContainer()

  useLayoutEffect(() => {
    setNav({
      pageId: PAGE_ID,
    })
  }, [setNav])

  const { complete } = loaderData.page.content

  const handleCreateFeedbackSuccessWithUrls = handleCreateFeedbackSuccess(
    complete,
    navigate
  )

  const { id: enrolmentId } = currentEnrolment

  const [, createFeedback] = useMutation(
    CreateInsomniaTherapyFeedbackMutationForEndOfTherapyMutation
  )

  const onComplete = handleFeedbackComplete(
    enrolmentId,
    createFeedback,
    displayError,
    handleCreateFeedbackSuccessWithUrls
  )

  return (
    <FeedbackEndOfTherapyPage
      content={loaderData.page.content}
      onComplete={onComplete}
    />
  )
}

export default FeedbackEndOfTherapyModule
